import React, { useState, useEffect } from 'react';
import {
  Layout,
  Typography,
  Card,
  Button,
  Flex,
  theme,
  Spin,
  message,
  Modal,
  Space,
  Tag,
  Empty,
  Tooltip,
  Dropdown,
  Menu,
} from 'antd';
import {
  PlusOutlined,
  CodeOutlined,
  DeleteOutlined,
  EyeOutlined,
  PlayCircleOutlined,
  ReloadOutlined,
  RobotOutlined,
  FolderOutlined,
  ArrowLeftOutlined,
} from '@ant-design/icons';
import { myAssistantServices } from '../../../../services/myAssistantServices';
import { CustomIntegration } from '../CustomIntegration/CustomIntegration';
import Editor from '@monaco-editor/react';

import { createLogger } from '../../../../../../utils/logger';
const logger = createLogger('features-assistants');

const { Content } = Layout;
const { Title, Text, Paragraph } = Typography;
const { useToken } = theme;

interface CustomIntegrationData {
  id: string;
  name: string;
  description: string;
  code: string;
  integrationId: string;
  flowId?: string;
  parameters: {
    type: string;
    metadata?: any;
    generatedCode: string;
  };
  createdAt: number;
  updatedAt: number;
}

interface CustomIntegrationSelectorProps {
  onBack: () => void;
  onSave: (data: any) => void;
  chatbotId?: string;
  flowId?: string;
}

export const CustomIntegrationSelector: React.FC<CustomIntegrationSelectorProps> = ({
  onBack,
  onSave,
  chatbotId,
  flowId,
}) => {
  const { token } = useToken();
  const [view, setView] = useState<'select' | 'list' | 'create'>('select');
  const [integrations, setIntegrations] = useState<CustomIntegrationData[]>([]);
  const [loading, setLoading] = useState(false);
  const [showCodeModal, setShowCodeModal] = useState(false);
  const [selectedIntegration, setSelectedIntegration] = useState<CustomIntegrationData | null>(null);
  const [deletingId, setDeletingId] = useState<string | null>(null);

  const loadIntegrations = async () => {
    if (!chatbotId) return;
    
    setLoading(true);
    try {
      // Usar el base path /integration para las lambdas
      const response = await fetch(`${process.env.REACT_APP_API_HOST}/integration/custom-integrations?chatbotId=${chatbotId}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const result = await response.json();
        logger.info('Custom integrations loaded:', result);
        setIntegrations(result.response.integrations || []);
      } else {
        logger.error('Error loading custom integrations:', response.status);
        message.error('Failed to load custom integrations');
      }
    } catch (error) {
      logger.error('Error loading custom integrations:', error);
      message.error('Failed to load custom integrations');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenFolder = () => {
    setView('list');
    loadIntegrations();
  };

  const handleCreateNew = () => {
    setView('create');
  };

  const handleBackToSelect = () => {
    setView('select');
  };

  const handleBackToList = () => {
    setView('list');
    loadIntegrations(); // Asegurar que se carguen las integraciones
  };

  const handleDeleteIntegration = async (integrationId: string) => {
    Modal.confirm({
      title: 'Delete Custom Integration',
      content: 'Are you sure you want to delete this custom integration? This action cannot be undone.',
      okText: 'Delete',
      okType: 'danger',
      cancelText: 'Cancel',
      onOk: async () => {
        setDeletingId(integrationId);
        try {
          // Usar el base path /integration para las lambdas
          const response = await fetch(`${process.env.REACT_APP_API_HOST}/integration/custom-integrations/${integrationId}`, {
            method: 'DELETE',
            headers: {
              'Content-Type': 'application/json',
            },
          });

          if (response.ok) {
            message.success('Custom integration deleted successfully');
            loadIntegrations(); // Reload the list
          } else {
            message.error('Failed to delete custom integration');
          }
        } catch (error) {
          logger.error('Error deleting custom integration:', error);
          message.error('Failed to delete custom integration');
        } finally {
          setDeletingId(null);
        }
      }
    });
  };

  const handleViewCode = (integration: CustomIntegrationData) => {
    setSelectedIntegration(integration);
    setShowCodeModal(true);
  };

  const handleTestIntegration = async (integration: CustomIntegrationData) => {
    try {
      const response = await fetch(`${process.env.REACT_APP_API_HOST}/integration/execute-custom-integration`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          integrationId: integration.id,
          parameters: {},
          context: {}
        }),
      });

      if (response.ok) {
        const result = await response.json();
        message.success('Integration test completed successfully');
        logger.info('Test result:', result);
      } else {
        message.error('Integration test failed');
      }
    } catch (error) {
      logger.error('Error testing integration:', error);
      message.error('Failed to test integration');
    }
  };

  const handleSelectIntegration = (integration: CustomIntegrationData) => {
    // Crear la información de la integración personalizada (similar a Oracle actions)
    const integrationInfo = {
      name: integration.name,
      description: integration.description,
      integrationId: integration.id,
      code: integration.code,
      parameters: integration.parameters,
      chatbotId: chatbotId,
      type: 'custom'
    };
    
    // Crear el mensaje con el formato estándar de integraciones (@integrationId/integrationId{...})
    const formattedMessage = `@${integration.id}/${integration.id}${JSON.stringify(integrationInfo)}`;
    
    onSave(formattedMessage);
  };

  const getDropdownItems = (integration: CustomIntegrationData) => [
    {
      key: 'view',
      label: 'View Code',
      icon: <EyeOutlined />,
      onClick: () => handleViewCode(integration)
    },
    {
      key: 'test',
      label: 'Test Integration',
      icon: <PlayCircleOutlined />,
      onClick: () => handleTestIntegration(integration)
    },
    {
      key: 'delete',
      label: 'Delete',
      icon: <DeleteOutlined />,
      danger: true,
      onClick: () => handleDeleteIntegration(integration.id)
    }
  ];

  const formatDate = (timestamp: number) => {
    return new Date(timestamp).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // View: Select Option
  if (view === 'select') {
    return (
      <Layout>
        <Content style={{ padding: token.padding }}>
          <Flex vertical gap={token.marginLG}>
            {/* Header */}
            <Flex align="center" gap={token.marginMD}>
              <Button
                type="link"
                icon={<ArrowLeftOutlined />}
                onClick={onBack}
                style={{ paddingLeft: 0 }}
              >
                Back
              </Button>
              <Title level={4} style={{ margin: 0 }}>
                Custom Integration
              </Title>
            </Flex>

            {/* Options */}
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
                gap: token.marginLG,
                maxWidth: '800px',
                margin: '0 auto',
              }}
            >
              {/* Open Folder Option */}
              <Card
                hoverable
                style={{ 
                  height: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  cursor: 'pointer'
                }}
                onClick={handleOpenFolder}
              >
                <div
                  style={{
                    background: token.colorPrimary,
                    borderRadius: '50%',
                    width: 64,
                    height: 64,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: token.marginMD,
                  }}
                >
                  <FolderOutlined style={{ color: 'white', fontSize: 32 }} />
                </div>
                <Title level={4} style={{ margin: 0, marginBottom: token.marginSM }}>
                  Open Custom Integrations
                </Title>
                <Text type="secondary">
                  View and manage your existing AI-generated custom integrations
                </Text>
              </Card>

              {/* Create New Option */}
              <Card
                hoverable
                style={{ 
                  height: '200px',
                  display: 'flex',
                  flexDirection: 'column',
                  justifyContent: 'center',
                  alignItems: 'center',
                  textAlign: 'center',
                  cursor: 'pointer'
                }}
                onClick={handleCreateNew}
              >
                <div
                  style={{
                    background: '#52c41a',
                    borderRadius: '50%',
                    width: 64,
                    height: 64,
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    marginBottom: token.marginMD,
                  }}
                >
                  <PlusOutlined style={{ color: 'white', fontSize: 32 }} />
                </div>
                <Title level={4} style={{ margin: 0, marginBottom: token.marginSM }}>
                  Create New Integration
                </Title>
                <Text type="secondary">
                  Generate a new custom integration with AI using files, websites, and prompts
                </Text>
              </Card>
            </div>
          </Flex>
        </Content>
      </Layout>
    );
  }

  // View: List Existing Integrations
  if (view === 'list') {
    return (
      <Layout>
        <Content style={{ padding: token.padding }}>
          <Flex vertical gap={token.marginLG}>
            {/* Header */}
            <Flex justify="space-between" align="center">
              <Flex align="center" gap={token.marginMD}>
                <Button
                  type="link"
                  icon={<ArrowLeftOutlined />}
                  onClick={handleBackToSelect}
                  style={{ paddingLeft: 0 }}
                >
                  Back
                </Button>
                <Title level={4} style={{ margin: 0 }}>
                  Custom Integrations
                </Title>
              </Flex>
              <Space>
                <Button
                  icon={<ReloadOutlined />}
                  onClick={loadIntegrations}
                  loading={loading}
                >
                  Refresh
                </Button>
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={handleCreateNew}
                >
                  Create New
                </Button>
              </Space>
            </Flex>

            {/* Integrations Grid */}
            {loading ? (
              <Flex justify="center" style={{ padding: token.paddingXL }}>
                <Spin size="large" />
              </Flex>
            ) : integrations.length === 0 ? (
              <Card>
                <Empty
                  image={<RobotOutlined style={{ fontSize: 64, color: token.colorTextSecondary }} />}
                  description="No custom integrations found"
                >
                  <Button type="primary" onClick={handleCreateNew}>
                    Create Your First Integration
                  </Button>
                </Empty>
              </Card>
            ) : (
              <div
                style={{
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(350px, 1fr))',
                  gap: token.marginMD,
                }}
              >
                {integrations.map((integration) => (
                  <Card
                    key={integration.id}
                    hoverable
                    style={{ height: '100%' }}
                    actions={[
                      <Tooltip title="Select Integration">
                        <Button
                          type="primary"
                          size="small"
                          onClick={() => handleSelectIntegration(integration)}
                        >
                          Select
                        </Button>
                      </Tooltip>,
                      <Tooltip title="View Code">
                        <EyeOutlined onClick={() => handleViewCode(integration)} />
                      </Tooltip>,
                      <Tooltip title="Test Integration">
                        <PlayCircleOutlined onClick={() => handleTestIntegration(integration)} />
                      </Tooltip>,
                      <Dropdown
                        menu={{ items: getDropdownItems(integration) }}
                        trigger={['click']}
                        placement="bottomRight"
                      >
                        <Button
                          type="text"
                          icon={<DeleteOutlined />}
                          loading={deletingId === integration.id}
                          danger
                        />
                      </Dropdown>
                    ]}
                  >
                    <Flex vertical gap={token.marginSM}>
                      <Flex justify="space-between" align="center">
                        <Flex align="center" gap={token.marginSM}>
                          <div
                            style={{
                              background: token.colorPrimary,
                              borderRadius: '50%',
                              width: 32,
                              height: 32,
                              display: 'flex',
                              justifyContent: 'center',
                              alignItems: 'center',
                            }}
                          >
                            <CodeOutlined style={{ color: 'white', fontSize: 16 }} />
                          </div>
                          <Title level={5} style={{ margin: 0 }}>
                            {integration.name}
                          </Title>
                        </Flex>
                        <Tag color="blue">Custom</Tag>
                      </Flex>
                      
                      <Paragraph type="secondary" style={{ margin: 0 }}>
                        {integration.description}
                      </Paragraph>
                      
                      <Flex justify="space-between" align="center">
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Created: {formatDate(integration.createdAt)}
                        </Text>
                        <Text type="secondary" style={{ fontSize: 12 }}>
                          Updated: {formatDate(integration.updatedAt)}
                        </Text>
                      </Flex>
                    </Flex>
                  </Card>
                ))}
              </div>
            )}
          </Flex>

          {/* View Code Modal */}
          <Modal
            title={`Code: ${selectedIntegration?.name}`}
            open={showCodeModal}
            onCancel={() => setShowCodeModal(false)}
            footer={[
              <Button key="close" onClick={() => setShowCodeModal(false)}>
                Close
              </Button>,
              <Button
                key="test"
                type="primary"
                icon={<PlayCircleOutlined />}
                onClick={() => selectedIntegration && handleTestIntegration(selectedIntegration)}
              >
                Test Integration
              </Button>
            ]}
            width={1000}
            destroyOnClose
          >
            {selectedIntegration && (
              <Flex vertical gap={token.marginMD}>
                <Flex justify="space-between" align="center">
                  <Text type="secondary">
                    Created: {formatDate(selectedIntegration.createdAt)}
                  </Text>
                  <Text type="secondary">
                    Updated: {formatDate(selectedIntegration.updatedAt)}
                  </Text>
                </Flex>
                <div style={{ border: '1px solid #d9d9d9', borderRadius: 6 }}>
                  <Editor
                    height="500px"
                    defaultLanguage="javascript"
                    value={selectedIntegration.code}
                    theme="vs-dark"
                    options={{
                      readOnly: true,
                      minimap: { enabled: false },
                      fontSize: 13,
                      wordWrap: 'on',
                      automaticLayout: true,
                      scrollBeyondLastLine: false,
                      lineNumbers: 'on',
                      folding: true,
                      renderLineHighlight: 'all',
                    }}
                  />
                </div>
              </Flex>
            )}
          </Modal>
        </Content>
      </Layout>
    );
  }

  // View: Create New Integration
  if (view === 'create') {
    return (
      <CustomIntegration
        onBack={() => {
          setView('select'); // Te lleva al menú principal de Custom Integration
        }}
        onSave={onSave}
        chatbotId={chatbotId}
      />
    );
  }

  return null;
}; 